import * as React from "react";
import styled from "react-emotion";
import {searchStocksByTicker, getHedgingOutput} from "../../../api/models/Hedging";
import {Field, Control, Input, Icon} from "bloomer";
import {isEmpty} from "lodash";
import Card from "../../../elements/Card";
import {H2} from "../../../elements/Heading";
import SendGAEvent, {GAEventActions, GAEventModels} from "../../../utils/SendGAEvent";
import ErrorUtils from "../../../utils/ErrorUtils";
import {toast} from "react-toastify";
import ModelContentContainer from "../../../elements/ModelContentContainer";
import Spacer from "../../../elements/Spacer";
import InformationCard from "../../../elements/InformationCard";

const SearchResult = styled("div")`
  border-bottom: 1px solid lightgrey;
  display: flex;

  :hover {
    cursor: pointer;
    color: white !important;
    background: #2f3241 !important;
  }
`;

const SelectedStock = styled(H2)`
  margin: 0;
  margin-right: 5px;
`;

const InformationContainer = styled("div")`
  display: flex;
`;

const InformationHeading = styled("span")`
  font-weight: 700;
`;

const AffectedAsset = styled("span")`
  margin-left: 3px; 
  padding: 4px;
  border: 1px solid lightgrey;
  border-radius: 3px;
`;

const AffectedAssetDescription = styled("span")`
  padding: 4px;
`;

const Asset = styled("div")`
  margin: 10px;
`;

const NegCorrelation = styled("span")`
  border: 1px solid #2f3241;
  background-color: #2f3241;
  border-radius: 10px;
  color: red;
  padding: 5px;
`;

interface IStock {
    company_name: string;
    stock_exchange: string;
    ticker: string;
}

interface ICorrOutput {
    correlation: number;
    exp_company_name: string;
    exp_stock_exchange: string;
    exp_ticker: string;
}

interface ISelectorState {
    loading: boolean;
    searchPerformed: boolean;
    searchResults: IStock[];
    currentInput: string;
    selectedStock: IStock;
    corrOutput: ICorrOutput[];
}

class HedgingModel extends React.Component<{}, ISelectorState> {
    public constructor(props: {}) {
        super(props);
        this.state = {
            loading: false,
            searchPerformed: false,
            searchResults: [],
            currentInput: "",
            selectedStock: {
                company_name: "",
                stock_exchange: "",
                ticker: ""
            },
            corrOutput: []
        };
    }

    public componentDidMount() {
        SendGAEvent(GAEventActions.VIEW_MODEL, GAEventModels.HEDGING_TOOL);
    }

    public handleChange = async (event: React.FormEvent<HTMLInputElement>) => {
        const input = event.currentTarget.value;

        this.setState({
            currentInput: input,
            searchResults: null
        });

        if (input.length === 0) {
            this.setState({
                selectedStock: {
                    company_name: "",
                    stock_exchange: "",
                    ticker: ""
                }
            });
        }

        let stocks: any = "";
        if (isEmpty(input.trim())) {
            this.setState({searchPerformed: false});
        } else {
            this.setState({loading: true, searchPerformed: true});
            stocks = await searchStocksByTicker(input.trim());
            this.setState({loading: false});
        }

        // @ts-ignore:
        this.setState({searchResults: stocks});
    };

    public selectStock = async (company: IStock) => {
        let errorOccurred = false;

        const response = await getHedgingOutput(company.ticker, company.stock_exchange).catch((error) => {
            this.setState({
                loading: false,
                selectedStock: company,
                corrOutput: []
            });
            if (ErrorUtils.requestLimitExceeded(error.response)) {
                toast.error(ErrorUtils.messageContent.REQUEST_LIMIT_EXCEEDED);
            }
            errorOccurred = true;
        });

        if (errorOccurred) {
            return;
        }

        if (response) {
            this.setState({
                selectedStock: company,
                corrOutput: response.data
            });

            SendGAEvent(GAEventActions.CALL_MODEL, GAEventModels.HEDGING_TOOL);
        }
    };

    public render() {
        return (
            <React.Fragment>
                <ModelContentContainer>
                    <Card>
                        <Field>
                            <Control>
                                <Input
                                    type="text"
                                    placeholder="Enter stock ticker like 'AAPL'"
                                    value={this.state.currentInput}
                                    onChange={this.handleChange}
                                />
                            </Control>
                        </Field>
                        {
                            this.state.loading && <div>
                                <Spacer/>
                                <span>Loading...</span>
                                <Icon className="fas fa-spinner fa-spin"/>
                            </div>
                        }
                        {
                            !this.state.loading && this.state.searchPerformed && isEmpty(this.state.searchResults) &&
                            <div>
                                <Spacer/>
                                <span>Stock ticker not found</span>
                            </div>
                        }
                        {!isEmpty(this.state.searchResults) &&
                        isEmpty(this.state.selectedStock.company_name) &&
                        this.state.searchResults.map((result: IStock) => (
                            <SearchResult
                                key={result.company_name + Math.random()}
                                onClick={() => {
                                    this.selectStock(result);
                                }}
                            >
                                {result.company_name}
                            </SearchResult>
                        ))}

                        {!isEmpty(this.state.selectedStock.company_name) ? (
                            <React.Fragment>
                                <div style={{display: "flex", marginLeft: "5px"}}>
                                    <SelectedStock>
                                        {this.state.selectedStock.ticker}
                                    </SelectedStock>
                                    <SelectedStock> | </SelectedStock>
                                    <SelectedStock>
                                        {" "}
                                        {this.state.selectedStock.company_name.slice(0, 30)}{" "}
                                    </SelectedStock>
                                </div>
                                {
                                    !isEmpty(this.state.corrOutput) ? <InformationContainer>
                                        <InformationCard>
                                            <InformationHeading>Negatively Correlated Assets</InformationHeading>
                                            {this.state.corrOutput.map((e: ICorrOutput) => (
                                                <Asset>
                                                    <NegCorrelation>{e.correlation.toFixed(2)}</NegCorrelation>
                                                    {e.exp_ticker && (
                                                        <AffectedAsset>{e.exp_ticker}</AffectedAsset>
                                                    )}:
                                                    {e.exp_stock_exchange && (
                                                        <span>{e.exp_stock_exchange}</span>
                                                    )}
                                                    {e.exp_company_name && (
                                                        <AffectedAssetDescription>
                                                            {e.exp_company_name}
                                                        </AffectedAssetDescription>
                                                    )}
                                                </Asset>
                                            ))}
                                        </InformationCard>
                                    </InformationContainer> : <></>
                                }
                            </React.Fragment>
                        ) : (
                            ""
                        )}
                    </Card>
                </ModelContentContainer>
            </React.Fragment>
        );
    }
}

export default HedgingModel;



// WEBPACK FOOTER //
// ./src/routes/Dashboard/Hedging/HedgingModel.tsx